import { memo, useCallback, useMemo } from "react";
import type { SharedValue } from "react-native-reanimated";
import { themeRoundnessMultiplier } from "../../constants";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import PlayerFlat from "./PlayerFlat";
import PlayerIconButton from "./PlayerIconButton";
import PlayerTime from "./PlayerTime";
import PlayerWave from "./PlayerWave";

export const millisecondsToMinutesAndSeconds = (ms: number) => {
	const totalSeconds = Math.round(ms / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

export type AudioPlayerType = "flat" | "wave";

export type AudioPlayerState =
	| "notPlayed"
	| "inProgress"
	| "playOnce"
	| "paused"
	| "played"
	| "disabled"
	| "locked";

type Props = {
	uri: string;
	totalTimeInMilliseconds: number;
	currentTimeInSeconds: number;
	progressShared?: SharedValue<number>;
	isPlaying: boolean;
	handlePlayPause: () => void;
	isBuffering: boolean;
	isEnded: boolean;
	onSeek: (progress: number) => Promise<void>;
	hidden?: boolean;
	waveformWidth?: number;
	updateWaveformWidth?: (width: number) => void;
	showWaveform?: boolean;
	useReanimated?: boolean;
	disabled?: boolean;
	locked?: boolean;
	type?: AudioPlayerType;
	playOnce?: boolean;
	playOnceInteractive?: boolean;
	// Renders the non-interactive "played" state without any playback having
	// happened (e.g. audio already heard on an earlier item of the same set).
	forceCompleted?: boolean;
	borderRadius?: number;
};

const deriveState = ({
	disabled,
	locked,
	isEnded,
	playOnce,
	isPlaying,
	currentTimeInSeconds,
}: {
	disabled: boolean;
	locked: boolean;
	isEnded: boolean;
	playOnce: boolean;
	isPlaying: boolean;
	currentTimeInSeconds: number;
}): AudioPlayerState => {
	if (disabled) {
		return "disabled";
	}
	if (locked) {
		return "locked";
	}
	if (playOnce) {
		return "playOnce";
	}
	if (isEnded) {
		return "played";
	}
	if (isPlaying) {
		return "inProgress";
	}
	if (currentTimeInSeconds > 0) {
		return "paused";
	}
	return "notPlayed";
};

const AudioPlayer = ({
	uri,
	totalTimeInMilliseconds,
	currentTimeInSeconds,
	progressShared,
	isPlaying,
	handlePlayPause,
	isBuffering,
	isEnded,
	onSeek,
	hidden,
	waveformWidth,
	updateWaveformWidth,
	showWaveform,
	useReanimated = true,
	disabled = false,
	locked = false,
	type,
	playOnce = false,
	playOnceInteractive,
	forceCompleted = false,
	borderRadius,
}: Props) => {
	const theme = useAppTheme();

	const resolvedType: AudioPlayerType =
		type ?? (showWaveform === false ? "flat" : "wave");

	const effectiveIsEnded = isEnded || forceCompleted;
	const effectivePlayOnce = playOnce || forceCompleted;

	const state = useMemo(
		() =>
			deriveState({
				disabled,
				locked,
				isEnded: effectiveIsEnded,
				playOnce: effectivePlayOnce,
				isPlaying,
				currentTimeInSeconds,
			}),
		[
			disabled,
			locked,
			effectiveIsEnded,
			effectivePlayOnce,
			isPlaying,
			currentTimeInSeconds,
		],
	);

	const formattedCurrentTime = useMemo(
		() =>
			millisecondsToMinutesAndSeconds(
				effectiveIsEnded
					? totalTimeInMilliseconds
					: currentTimeInSeconds * 1000,
			),
		[effectiveIsEnded, currentTimeInSeconds, totalTimeInMilliseconds],
	);
	const formattedTotalTime = useMemo(
		() => millisecondsToMinutesAndSeconds(totalTimeInMilliseconds),
		[totalTimeInMilliseconds],
	);
	// A falsy duration before playback means metadata hasn't arrived yet
	// (avoids a "0:00 / 0:00" flash). Once playback starts, an unknown
	// duration (e.g. streams) must not keep the player stuck in loading.
	const hasStartedPlayback =
		isPlaying || effectiveIsEnded || currentTimeInSeconds > 0;
	const awaitingDuration = !(totalTimeInMilliseconds || hasStartedPlayback);
	const isLoading = useMemo(
		() => (isBuffering || awaitingDuration) && !forceCompleted,
		[isBuffering, awaitingDuration, forceCompleted],
	);
	const fallbackProgress = useMemo(() => {
		if (effectiveIsEnded) {
			return 1;
		}
		return totalTimeInMilliseconds
			? (currentTimeInSeconds * 1000) / totalTimeInMilliseconds
			: 0;
	}, [effectiveIsEnded, currentTimeInSeconds, totalTimeInMilliseconds]);
	const progressText = `${formattedCurrentTime} / ${formattedTotalTime}`;

	const handleSeek = useCallback(
		async (newProgress: number) => {
			await onSeek(newProgress * totalTimeInMilliseconds);
		},
		[totalTimeInMilliseconds, onSeek],
	);

	const isStateDisabled = state === "disabled";
	const isSeekable =
		state !== "disabled" && state !== "locked" && state !== "playOnce";

	return (
		<Box
			borderRadius={borderRadius ?? theme.roundness * themeRoundnessMultiplier}
			backgroundColor={
				isStateDisabled
					? theme.colors.elevation.level3
					: theme.colors.secondaryContainer
			}
			flexDirection="row"
			alignItems="center"
			width="100%"
			paddingHorizontal={16}
			paddingVertical={12}
			minHeight={72}
			gap={16}
			display={hidden ? "none" : "flex"}
		>
			<PlayerIconButton
				state={state}
				isEnded={effectiveIsEnded}
				isPlaying={isPlaying}
				isLoading={isLoading}
				onPress={handlePlayPause}
				playOnceInteractive={playOnceInteractive}
			/>
			{resolvedType === "wave" ? (
				<PlayerWave
					uri={uri}
					progress={fallbackProgress}
					progressShared={progressShared}
					onSeek={handleSeek}
					waveformWidth={waveformWidth}
					updateWaveformWidth={updateWaveformWidth}
					useReanimated={useReanimated}
					canSeek={isSeekable}
				/>
			) : (
				<PlayerFlat
					progress={fallbackProgress}
					state={state}
					isEnded={effectiveIsEnded}
				/>
			)}
			<PlayerTime
				text={progressText}
				isLoading={isLoading}
				isDisabled={isStateDisabled}
			/>
		</Box>
	);
};

export default memo(AudioPlayer);
